import { api } from "./client";
import { newIdempotencyKey } from "./orders";
import type {
  AdjustResult,
  InventoryAvailability,
  InventoryFamily,
  InventoryLifecycle,
  InventoryListPage,
  ManualReasonCode,
  MovementHistory,
  ThresholdResult,
} from "./types";

/** Query parameters the admin inventory listing actually accepts.
 *
 * Every one of them is applied by the server. There is no client-side
 * narrowing anywhere in this module or the screens that use it: the result is
 * one page, so a filter applied here would search the twenty-five rows in
 * front of the administrator and report that an item on page two does not
 * exist.
 */
export interface InventoryListQuery {
  /** Product name, the shop's product code, or the variant name. */
  search?: string;
  family?: InventoryFamily;
  availability?: InventoryAvailability;
  lifecycle?: InventoryLifecycle;
  page?: number;
  page_size?: number;
}

/**
 * What an operator DID, which is the only kind of stock command there is.
 *
 * There is no "set stock to N". A total is a number read off a screen that may
 * already be stale, and writing it would silently discard whatever happened in
 * between; a delta plus the quantity the operator was looking at lets the
 * server refuse rather than overwrite.
 */
export interface AdjustBody {
  operation: "add" | "remove";
  quantity: number;
  /** What the screen was showing. A mismatch is a 409, never an adjustment
   *  applied to a number the operator never saw. */
  expected_stock_quantity: number;
  reason_code: ManualReasonCode;
  /** Required and non-blank when reason_code is "other". */
  note?: string | null;
}

export const inventoryApi = {
  list: (query: InventoryListQuery, signal?: AbortSignal) =>
    api.get<InventoryListPage>("/admin/inventory", { ...query }, signal),

  history: (itemId: string, signal?: AbortSignal) =>
    api.get<MovementHistory>(
      `/admin/inventory/${itemId}/history`,
      undefined,
      signal,
    ),

  /**
   * Move stock once.
   *
   * The idempotency key is generated per call, and this client never retries a
   * mutation on its own — so a second key only ever exists because an
   * administrator pressed the button again, which IS a second intent. What the
   * key protects against is the request that was delivered and whose answer was
   * lost.
   */
  adjust: (itemId: string, body: AdjustBody, idempotencyKey?: string) =>
    api.post<AdjustResult>(`/admin/inventory/${itemId}/adjust`, body, {
      "Idempotency-Key": idempotencyKey ?? newIdempotencyKey(),
    }),

  /**
   * Configure when this item starts warning, or switch the warning off.
   *
   * `low_stock_threshold: null` clears it and is a real instruction. Zero is a
   * different one: warn only when it has actually run out. No idempotency key —
   * setting a value to a value is naturally repeatable, and `expected_version`
   * is what stops a stale screen overwriting somebody else's choice.
   */
  setThreshold: (
    itemId: string,
    expectedVersion: number,
    threshold: number | null,
  ) =>
    api.post<ThresholdResult>(
      `/admin/inventory/${itemId}/low-stock-threshold`,
      { expected_version: expectedVersion, low_stock_threshold: threshold },
    ),
};
